import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { useToast } from './Toast';
import { Modal, Button, Input, Select, Textarea, Badge, LoadingState, EmptyState, Card } from './ui';

const PRIORITY_VARIANTS = {
  low: 'success',
  medium: 'warning',
  high: 'danger',
  urgent: 'danger',
};

const PRIORITY_LABELS = {
  low: '低優先度',
  medium: '中優先度',
  high: '高優先度',
  urgent: '緊急',
};

export default function EngineerMemo({ engineerName, onClose, onMemoChange }) {
  const toast = useToast();
  const [memos, setMemos] = useState([]);
  const [newMemo, setNewMemo] = useState({
    title: '',
    content: '',
    priority: 'medium',
    tags: '',
    due_date: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useUser();

  const API_BASE = '/api/memos/';

  const fetchMemos = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE}by_engineer/?engineer_name=${encodeURIComponent(engineerName)}`);
      if (response.ok) {
        const data = await response.json();
        setMemos(data);
      }
    } catch (error) {
      console.error('メモ取得エラー:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const createMemo = async () => {
    if (!newMemo.title.trim() || !newMemo.content.trim()) {
      toast.warning('タイトルと内容を入力してください');
      return;
    }

    try {
      setIsSubmitting(true);
      const memoData = {
        ...newMemo,
        memo_type: 'engineer',
        engineer_name: engineerName,
        author: user?.name || user?.email || 'Anonymous',
        tags: newMemo.tags ? newMemo.tags.split(',').map(tag => tag.trim()) : []
      };

      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(memoData),
      });

      if (response.ok) {
        setNewMemo({
          title: '',
          content: '',
          priority: 'medium',
          tags: '',
          due_date: ''
        });
        await fetchMemos();
        onMemoChange && onMemoChange();
      }
    } catch (error) {
      console.error('メモ作成エラー:', error);
      toast.error('メモの作成に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMemoCompletion = async (memoId, currentStatus) => {
    try {
      const response = await fetch(`${API_BASE}${memoId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          is_completed: !currentStatus
        }),
      });

      if (response.ok) {
        await fetchMemos();
        onMemoChange && onMemoChange();
      }
    } catch (error) {
      console.error('メモ更新エラー:', error);
    }
  };

  const deleteMemo = async (memoId) => {
    if (!window.confirm('このメモを削除しますか？')) return;

    try {
      const response = await fetch(`${API_BASE}${memoId}/`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await fetchMemos();
        onMemoChange && onMemoChange();
      }
    } catch (error) {
      console.error('メモ削除エラー:', error);
    }
  };

  useEffect(() => {
    fetchMemos();
  }, [engineerName]);

  return (
    <Modal
      open
      onClose={onClose}
      title={`${engineerName} の営業メモ`}
      size="xl"
      hideFooter
    >
      <Card className="mb-6 bg-prodia-background" padding>
        <h4 className="text-base font-semibold text-prodia-text-primary mb-4">
          <i className="fas fa-plus-circle mr-2 text-prodia-primary" />
          新規メモ作成
        </h4>
        
        <div className="space-y-4">
          <Input
            label={<>タイトル <span className="text-danger">*</span></>}
            type="text"
            value={newMemo.title}
            onChange={(e) => setNewMemo({...newMemo, title: e.target.value})}
            placeholder="例: 面談予定、条件交渉、案件提案"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="優先度"
              value={newMemo.priority}
              onChange={(e) => setNewMemo({...newMemo, priority: e.target.value})}
            >
              <option value="low">低</option>
              <option value="medium">中</option>
              <option value="high">高</option>
              <option value="urgent">緊急</option>
            </Select>

            <Input
              label="期限"
              type="datetime-local"
              value={newMemo.due_date}
              onChange={(e) => setNewMemo({...newMemo, due_date: e.target.value})}
            />
          </div>

          <Input
            label="タグ（カンマ区切り）"
            type="text"
            value={newMemo.tags}
            onChange={(e) => setNewMemo({...newMemo, tags: e.target.value})}
            placeholder="例: 面談済み, 条件交渉中, 案件マッチング"
          />

          <Textarea
            label={<>内容 <span className="text-danger">*</span></>}
            value={newMemo.content}
            onChange={(e) => setNewMemo({...newMemo, content: e.target.value})}
            placeholder="詳細な内容を入力してください..."
            rows={4}
          />

          <Button onClick={createMemo} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <i className="fas fa-spinner fa-spin" />
                作成中...
              </>
            ) : (
              <>
                <i className="fas fa-save" />
                メモを作成
              </>
            )}
          </Button>
        </div>
      </Card>

      <div>
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-base font-semibold text-prodia-text-primary flex items-center gap-2">
            <i className="fas fa-list-alt text-prodia-primary" />
            既存メモ詳細 ({memos.length}件)
          </h4>
          {memos.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-prodia-text-secondary">
              <Badge variant="danger">
                緊急・高: {memos.filter(m => ['urgent', 'high'].includes(m.priority) && !m.is_completed).length}
              </Badge>
              <Badge variant="warning">
                未完了: {memos.filter(m => !m.is_completed).length}
              </Badge>
            </div>
          )}
        </div>

        {isLoading ? (
          <LoadingState message="メモを読み込み中..." />
        ) : memos.length === 0 ? (
          <EmptyState
            icon="fas fa-sticky-note"
            title="まだメモがありません"
            description="上記のフォームから最初のメモを作成してください"
          />
        ) : (
          <div className="space-y-4">
            {memos
              .sort((a, b) => {
                if (a.is_completed !== b.is_completed) {
                  return a.is_completed ? 1 : -1;
                }
                const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
                if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
                  return priorityOrder[b.priority] - priorityOrder[a.priority];
                }
                return new Date(b.created_at) - new Date(a.created_at);
              })
              .map(memo => (
                <Card
                  key={memo.id}
                  className={`transition-all ${
                    memo.is_completed ? 'opacity-70 bg-prodia-background' : ''
                  }`}
                  padding
                >
                  <div className="border-b border-prodia-border pb-4 mb-4">
                    <div className="flex justify-between items-start mb-3 gap-3 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h5 className={`text-lg font-bold ${
                          memo.is_completed ? 'line-through text-prodia-text-muted' : 'text-prodia-text-primary'
                        }`}>
                          {memo.title}
                        </h5>
                        <Badge variant={PRIORITY_VARIANTS[memo.priority]}>
                          {PRIORITY_LABELS[memo.priority]}
                        </Badge>
                        {memo.is_completed && (
                          <Badge variant="success" icon="fas fa-check">
                            完了済み
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant={memo.is_completed ? 'secondary' : 'primary'}
                          onClick={() => toggleMemoCompletion(memo.id, memo.is_completed)}
                        >
                          <i className={`fas ${memo.is_completed ? 'fa-undo' : 'fa-check'}`} />
                          {memo.is_completed ? '未完了に戻す' : '完了にする'}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => deleteMemo(memo.id)}
                        >
                          <i className="fas fa-trash" />
                          削除
                        </Button>
                      </div>
                    </div>

                    {memo.due_date && (
                      <div className={`flex items-center gap-2 text-sm font-medium ${
                        new Date(memo.due_date) < new Date() && !memo.is_completed
                          ? 'text-danger'
                          : 'text-warning-foreground'
                      }`}>
                        <i className="fas fa-clock" />
                        <span>期限: {new Date(memo.due_date).toLocaleString('ja-JP')}</span>
                        {new Date(memo.due_date) < new Date() && !memo.is_completed && (
                          <Badge variant="danger">期限超過</Badge>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <div className={`text-sm leading-relaxed mb-4 ${
                      memo.is_completed ? 'text-prodia-text-muted' : 'text-prodia-text-primary'
                    }`}>
                      <div className="bg-prodia-background p-4 rounded-lg border border-prodia-border">
                        {memo.content.split('\n').map((line, index) => (
                          <p key={index} className="mb-2 last:mb-0">{line}</p>
                        ))}
                      </div>
                    </div>

                    {memo.tags && memo.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="text-sm font-medium text-prodia-text-secondary mr-1">タグ:</span>
                        {memo.tags.map((tag, index) => (
                          <Badge key={index} variant="info" icon="fas fa-tag">
                            #{tag}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div className="flex justify-between items-center text-sm text-prodia-text-muted bg-prodia-background p-3 rounded-lg border border-prodia-border flex-wrap gap-2">
                      <div className="flex items-center gap-4 flex-wrap">
                        <span className="flex items-center gap-1">
                          <i className="fas fa-user" />
                          作成者: <span className="font-medium text-prodia-text-secondary">{memo.author}</span>
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="fas fa-calendar-plus" />
                          作成: {new Date(memo.created_at).toLocaleString('ja-JP')}
                        </span>
                      </div>
                      {memo.updated_at !== memo.created_at && (
                        <span className="flex items-center gap-1">
                          <i className="fas fa-edit" />
                          更新: {new Date(memo.updated_at).toLocaleString('ja-JP')}
                        </span>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
